import * as React from "react";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { TextBoxEditor } from "../../../../../../../react-core/Html/TextBoxEditor";
import {
    BankAccountOwnerArrays,
    BankAccountOwnerMap,
    ContentsTable,
} from "../../../NewBusinessWizard/Resource/Contents";
import { ValidatorContents } from "../../../../../Common/ValidatorHelper";
import { BankAccountOwner } from "../../../../../../../Wrap/Constants/Enums/Domain/BankAccountOwner";
import { AlphanumericWithSpecialCharacters, Numeric, Numeric2 } from "../../../../../../../react-core/Html/Validation/RegExPatterns";
import { BankAccountInstruction } from "../../../../../../../Wrap/Models/Domains/Instructions/BankAccountInstruction";
import { InstructionStatus } from "../../../../../../../Wrap/Constants/Enums/Domain/InstructionStatus";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { PaymentType } from "../../Resource/Contents";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { DirectDebitMethod } from "../../../../../../../Wrap/Constants/Enums/Domain/DirectDebitMethod";
import { Lightbox, LightboxType } from "../../../../../../../react-core/Html/Components/Lightbox";
import { DirectDebitTerms } from "../WrapCash/DirectDebitTerms";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { Action } from "../../../../../Actions/Action";
import { BwaBankAccountValidationRequest } from "../../../../../../../Wrap/Models/Domains/BankAccount/BwaBankAccountValidationRequest";
import { BwaBranchDataReferral } from "../../../../../../../Wrap/Models/Domains/BankAccount/BwaBranchDataReferral";
import { BankAccountErrorPopupView } from "./BankAccountErrorPopupView";
import { BankAccountSuccessPopupView } from "./BankAccountSuccessPopupView";
import { Address } from "../../../../../../../Wrap/Models/Domains/Address/Address";
import { CountryCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CountryCode";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";

interface INewBankAccountInstructionProps extends IProcessComponentProps {
    payeeType: PaymentType;
    withdrawalsOnly?: boolean;
    onChange: (bankAccountInstruction: BankAccountInstruction) => void;
    data: BankAccountInstruction;
    customerId?: number;
}

interface INewBankAccountInstructionState {
    Owner: BankAccountOwner;
    AccountName: string;
    AccountNumber: string;
    RollNumber: string;
    BankCode: string;
    BranchCode: string;
    ExtraCode: string;
    NominatedForDirectDebit: boolean;
    NominatedForWithdrawal: boolean;
    DirectDebitMethod: DirectDebitMethod;
    onlineDeclarationConfirmed: boolean;
    showOnlineDeclarationPopup: boolean;
    isBWValidateSuccess: boolean;
    BWvalidationMssage: string[];
    showBWValidatePopup: boolean;
    showErrorMsgPopup: boolean;
    branchdata: BwaBranchDataReferral;
    findBtnCanClick: boolean;
}

export class NewBankAccountInstruction extends ProcessComponent<
    INewBankAccountInstructionProps,
    INewBankAccountInstructionState
> {
    constructor(props: INewBankAccountInstructionProps) {
        super(props);
        this.state = {
            Owner: props.data?.Owner,
            AccountName: null,
            AccountNumber: props.data?.AccountNumber,
            RollNumber: null,
            BankCode: props.data?.BankCodes?.BankCode,
            BranchCode: props.data?.BankCodes?.BranchCode,
            ExtraCode: props.data?.BankCodes?.ExtraCode,
            NominatedForDirectDebit: false,
            NominatedForWithdrawal: props.data?.NominatedForWithdrawal,
            DirectDebitMethod: this.isPayeeeTypeThirdPartyOrEmployerOrEmployerSalaryDeduction()
                ? DirectDebitMethod.Paper
                : DirectDebitMethod.NotSpecified,
            onlineDeclarationConfirmed: false,
            showOnlineDeclarationPopup: false,
            showErrorMsgPopup: false,
            isBWValidateSuccess: false,
            BWvalidationMssage: [],
            showBWValidatePopup: false,
            branchdata: null,
            findBtnCanClick: true,
        };
    }

    private isPayeeeTypeThirdPartyOrEmployerOrEmployerSalaryDeduction(): boolean {
        const { payeeType } = this.props;
        return payeeType == PaymentType.Employer || payeeType == PaymentType.ThirdParty || payeeType == PaymentType.EmployerSalaryDeduction;
    }

    private onCallBWValidateService(): boolean {
        const { AccountNumber, BankCode, BranchCode, ExtraCode, isBWValidateSuccess } = this.state;
        const { customerId } = this.props;
        const sortCode = (BankCode ?? "00") + (BranchCode ?? "00") + (ExtraCode ?? "00");

        if (CommonHelper.isNullOrEmpty(AccountNumber) || CommonHelper.isNullOrEmpty(sortCode)) {
            this.setState({
                BWvalidationMssage: ["Account number or Sort code can't be empty"],
            });
            return false;
        } else {
            this.setState({ findBtnCanClick: false });
            const request: BwaBankAccountValidationRequest = {
                AccountNumber: AccountNumber,
                SortCode: sortCode,
                InstructionId: customerId ? customerId : getQueryParameters()["customerid"],
            };
            Action.ValidateExperianBankAccount(request, (response: BwaBranchDataReferral) => {
                this.setState({
                    BWvalidationMssage: response.ValidateStatus
                        ? []
                        : [
                              "No bank information found for entered details. No further checks will be carried out on this bank account.",
                          ],
                    isBWValidateSuccess: response.ValidateStatus,
                    showBWValidatePopup: response.ValidateStatus,
                    showErrorMsgPopup: !response.ValidateStatus,
                    branchdata: response,
                    findBtnCanClick: true,
                });
            });
            return true;
        }
    }

    public render() {
        const { data, withdrawalsOnly } = this.props;
        const { isBWValidateSuccess, BWvalidationMssage, branchdata, AccountNumber, findBtnCanClick } = this.state;

        return (
            <div>
                {BWvalidationMssage.length > 0 &&
                    BWvalidationMssage.map((m, i) => <Alert key={i} alertType={AlertType.Error} message={m}></Alert>)}
                <div className="row">
                    <div className="col-sm-4 col-12">
                        <h4>Bank account details</h4>
                        <SelectEditor
                            options={[null, ...this.getBankAccountOwners()]}
                            getText={(v) => BankAccountOwnerMap[v]}
                            required="Please select owner"
                            selected={data?.Owner}
                            label="Owner"
                            onChange={(v) => this.setState({ Owner: v })}
                        />
                        <TextBoxEditor
                            label="Account name"
                            required="Please enter Account name"
                            value={data?.AccountName}
                            maxLength={45}
                            regex={{
                                errorMessage: ValidatorContents.AlphaNumericWithSpecialCharacters,
                                value: AlphanumericWithSpecialCharacters,
                            }}
                            onChange={(v) => this.setState({ AccountName: v })}
                        />
                        <TextBoxEditor
                            label="Account number"
                            required="Please enter account number"
                            value={AccountNumber}
                            maxLength={8}
                            regex={{ errorMessage: "Please enter digits", value: Numeric }}
                            validationRules={[
                                {
                                    errorMessage: ValidatorContents.BankAccountNumberMustBeEightDigits,
                                    validator: (v) => {
                                        return v?.length === 8;
                                    },
                                },
                            ]}
                            onChange={(v) => this.setState({ AccountNumber: v })}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-4  col-12">{this.renderSortCodeEditor()}</div>
                    {!this.props.readonly && (
                        <div className="col-sm-4 col-12">
                            <legend className="control-label">&nbsp;</legend>
                            <div className="row narrow-gutter">
                                <button
                                    className="btn secondary"
                                    disabled={!findBtnCanClick}
                                    onClick={() => {
                                        this.onCallBWValidateService();
                                    }}
                                >
                                    Find
                                </button>
                            </div>
                        </div>
                    )}
                    {this.renderBankAccoutErrorPopUp()}
                    {this.renderBankAccoutSuccessPopUp()}
                </div>
                <div className="row">
                    <div className="col-sm-4 col-12">
                        <TextBoxEditor
                            label="Building society roll number (optional)"
                            value={data?.RollNumber}
                            onChange={(v) => this.setState({ RollNumber: v })}
                        />
                        <CheckboxEditor
                            value={data?.NominatedForDirectDebit}
                            onChange={(v) => this.setState({ NominatedForDirectDebit: v })}
                            label="Direct Debit instruction"
                            cssWrapper="square-checkbox block"
                        />
                        {this.state.Owner != null &&
                            this.state.Owner != BankAccountOwner.Employer &&
                            this.state.Owner != BankAccountOwner.ThirdParty && (
                                <CheckboxEditor
                                    value={data?.NominatedForWithdrawal}
                                    onChange={(v) => this.setState({ NominatedForWithdrawal: v })}
                                    label="Nominated account for withdrawals"
                                    cssWrapper="square-checkbox block"
                                    disabled={withdrawalsOnly}
                                />
                            )}
                    </div>
                    <div className="col-12">{this.renderDirectDebitMethod()}</div>
                </div>
                {!this.props.readonly && (
                    <div className="row vspace-top-24">
                        <div className="col-auto">
                            <button
                                className="btn secondary"
                                onClick={() => {
                                    if (this.isValid()) {
                                        if (isBWValidateSuccess) {
                                            this.props.onChange({
                                                AccountName: this.state.AccountName,
                                                AccountNumber: this.state.AccountNumber,
                                                BankCodes: {
                                                    BankCode: this.state.BankCode,
                                                    BranchCode: this.state.BranchCode,
                                                    ExtraCode: this.state.ExtraCode,
                                                },
                                                BankDetails: {
                                                    Name: branchdata?.InstitutionName,
                                                    Address: this.setAddress(branchdata),
                                                },
                                                Owner: this.state.Owner,
                                                RollNumber: this.state.RollNumber,
                                                NominatedForWithdrawal: this.state.NominatedForWithdrawal,
                                                NominatedForDirectDebit: this.state.NominatedForDirectDebit,
                                                DirectDebitMethod: this.state.DirectDebitMethod,
                                                Status: InstructionStatus.Pending,
                                            });
                                        } else {
                                            this.setState({
                                                BWvalidationMssage: [
                                                    "Please click find button to process BWValidatation.",
                                                ],
                                            });
                                        }
                                    }
                                }}
                            >
                                Save bank account
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }
    renderSortCodeEditor() {
        const { AccountNumber, BankCode, BranchCode, ExtraCode, isBWValidateSuccess } = this.state;
        return (
            <fieldset>
                <legend className="control-label">Sort code</legend>
                <div className="row narrow-gutter">
                    <div className="col-4">
                        <TextBoxEditor
                            cssWrapper="control-size-60"
                            cssInput="text-center"
                            required="Required"
                            placeholder="00"
                            textLength={2}
                            value={BankCode}
                            regex={{ errorMessage: ValidatorContents.Numeric2, value: Numeric2 }}
                            // validationRules={[
                            //     {
                            //         errorMessage: ValidatorContents.NonZeroSortCode,
                            //         validator: (v) => {
                            //             return parseInt(v) !== 0;
                            //         },
                            //     },
                            // ]}
                            onChange={(v) => {
                                if (v) {
                                    this.setState({ BankCode: v });
                                }
                            }}
                        />
                    </div>
                    <div className="col-4">
                        <TextBoxEditor
                            cssWrapper="control-size-60"
                            cssInput="text-center"
                            required="Required"
                            placeholder="00"
                            textLength={2}
                            value={BranchCode}
                            regex={{ errorMessage: ValidatorContents.Numeric2, value: Numeric2 }}
                            onChange={(v) => {
                                if (v) {
                                    this.setState({ BranchCode: v });
                                }
                            }}
                        />
                    </div>
                    <div className="col-4">
                        <TextBoxEditor
                            cssWrapper="control-size-60"
                            cssInput="text-center"
                            required="Required"
                            placeholder="00"
                            textLength={2}
                            value={ExtraCode}
                            regex={{ errorMessage: ValidatorContents.Numeric2, value: Numeric2 }}
                            onChange={(v) => {
                                if (v) {
                                    this.setState({ ExtraCode: v });
                                }
                            }}
                        />
                    </div>
                </div>
            </fieldset>
        );
    }

    private setAddress(branchdata: BwaBranchDataReferral): Address {
        return {
            Line1: branchdata?.AddressLines?.[0],
            Line2: branchdata?.AddressLines?.[1],
            Line3: branchdata?.AddressLines?.[2],
            Line4: branchdata?.AddressLines?.[3],
            Postcode: branchdata?.PostOrZipCode,
            CountryCode: CountryCode.GBR,
        };
    }

    private renderBankAccoutErrorPopUp(): JSX.Element {
        const { showErrorMsgPopup, BWvalidationMssage } = this.state;
        if (showErrorMsgPopup) {
            return (
                <BankAccountErrorPopupView
                    showBox={showErrorMsgPopup}
                    data={BWvalidationMssage}
                    onClose={() => this.setState({ showErrorMsgPopup: false })}
                    onClick={() =>
                        this.setState({
                            BankCode: null,
                            BranchCode: null,
                            ExtraCode: null,
                            isBWValidateSuccess: false,
                            showErrorMsgPopup: false,
                            AccountNumber: null,
                            BWvalidationMssage: [],
                        })
                    }
                />
            );
        }
        return null;
    }

    private renderBankAccoutSuccessPopUp(): JSX.Element {
        const { showBWValidatePopup, branchdata } = this.state;
        if (showBWValidatePopup) {
            return (
                <BankAccountSuccessPopupView
                    showBox={showBWValidatePopup}
                    data={branchdata}
                    onClose={() => this.setState({ showBWValidatePopup: false })}
                    onCancelClick={() =>
                        this.setState({
                            showBWValidatePopup: false,
                            BankCode: null,
                            BranchCode: null,
                            ExtraCode: null,
                            isBWValidateSuccess: false,
                        })
                    }
                    onContinueClick={() => this.setState({ showBWValidatePopup: false })}
                />
            );
        }
        return null;
    }

    private getBankAccountOwners(): BankAccountOwner[] {
        const { payeeType } = this.props;
        if (payeeType === PaymentType.Client) {
            return BankAccountOwnerArrays.filter((u) => u !== BankAccountOwner.ThirdParty);
        }
        if (payeeType === PaymentType.ClientIncome) {
            return [BankAccountOwner.Individual,BankAccountOwner.Joint];
        }
        if (payeeType === PaymentType.ThirdParty) {
            return [BankAccountOwner.ThirdParty];
        }
        if (payeeType === PaymentType.Employer || payeeType === PaymentType.EmployerSalaryDeduction) {
            return [BankAccountOwner.Employer];
        }
        if (payeeType === PaymentType.AssumedBond) {
            return BankAccountOwnerArrays.filter((u) => u !== BankAccountOwner.ThirdParty && u !== BankAccountOwner.Employer);
        }
    }

    private renderDirectDebitMethod() {
        const { data } = this.props;

        return (
            <>
                <RadiosEditor
                    tooltip={`${ContentsTable.DirectDebitMethodInformation1}              
                    ${ContentsTable.DirectDebitMethodInformation2}
                    ${ContentsTable.DirectDebitMethodInformation3}`}
                    showTooltipCloseButton={true}
                    cssWrapper="rg-150 mb-20"
                    options={[DirectDebitMethod.Online, DirectDebitMethod.Paper]}
                    getText={(v) => (v == DirectDebitMethod.Online ? "Online" : "Paper")}
                    onChange={(v) => this.setState({ DirectDebitMethod: v })}
                    label={"Direct Debit"}
                    selected={
                        this.isPayeeeTypeThirdPartyOrEmployerOrEmployerSalaryDeduction() ? this.state.DirectDebitMethod : data?.DirectDebitMethod
                    }
                    disabled={
                        this.props.payeeType == PaymentType.Employer || 
                        this.props.payeeType == PaymentType.ThirdParty ||
                        this.props.payeeType == PaymentType.EmployerSalaryDeduction
                    }
                    required="Please select Direct Debit"
                />
                <div>
                    {this.state.DirectDebitMethod == DirectDebitMethod.Online && (
                        <>
                            <button
                                type="button"
                                className="hyperlink mb-15"
                                onClick={(v) => this.setState({ showOnlineDeclarationPopup: true })}
                            >
                                Direct Debit terms
                            </button>
                            <CheckboxEditor
                                value={this.state.onlineDeclarationConfirmed}
                                onChange={(v) => this.setState({ onlineDeclarationConfirmed: v })}
                                label="I confirm that I understand and agree to the Online Direct Debit terms"
                                cssWrapper="square-checkbox block"
                                required="You must complete the required declaration"
                            />
                        </>
                    )}

                    {this.state.DirectDebitMethod == DirectDebitMethod.Online && this.state.showOnlineDeclarationPopup && (
                        <Lightbox
                            show={this.state.showOnlineDeclarationPopup}
                            title={"Confirmation"}
                            contentAfterTitle={null}
                            onClose={() => this.setState({ showOnlineDeclarationPopup: false })}
                            type={LightboxType.Modal}
                            unclosable={true}
                            noOverlay={false}
                            noAutofocus={true}
                            cssWrapper="is-default-lightbox"
                        >
                            <DirectDebitTerms />
                            <button
                                className="btn primary"
                                onClick={() => this.setState({ showOnlineDeclarationPopup: false })}
                            >
                                Confirm
                            </button>
                        </Lightbox>
                    )}
                </div>
            </>
        );
    }
}
